import React, { useState, useEffect } from 'react';
import { Coupon, CouponSettings, CouponCreateData, CouponUpdateData } from '../../types';
import { couponService } from '../../services/couponService';
import CouponManager from '../../components/organisms/CouponManager';
import './CouponsPage.css';

const CouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [settings, setSettings] = useState<CouponSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load coupons and settings
  useEffect(() => {
    loadCouponsData();
  }, []);

  const loadCouponsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [couponsData, settingsData] = await Promise.all([
        couponService.getCoupons(),
        couponService.getCouponSettings()
      ]);

      setCoupons(couponsData);
      setSettings(settingsData);
    } catch (err) {
      console.error('Error loading coupons data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load coupons data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (couponData: CouponCreateData) => {
    try {
      // For now, use a placeholder createdBy ID
      // In a real app, this would come from the authenticated user
      const createdBy = 'admin-user-id';
      
      const newCoupon = await couponService.createCoupon(couponData, createdBy);
      setCoupons(prev => [newCoupon, ...prev]);
    } catch (err) {
      console.error('Error creating coupon:', err);
      throw err;
    }
  };

  const handleUpdateCoupon = async (couponId: string, updates: CouponUpdateData) => {
    try {
      const updatedCoupon = await couponService.updateCoupon(couponId, updates);
      setCoupons(prev => 
        prev.map(coupon => 
          coupon.id === couponId ? updatedCoupon : coupon
        )
      );
    } catch (err) {
      console.error('Error updating coupon:', err);
      throw err;
    }
  };

  const handleDeleteCoupon = async (couponId: string) => {
    try {
      await couponService.deleteCoupon(couponId);
      setCoupons(prev => prev.filter(coupon => coupon.id !== couponId));
    } catch (err) {
      console.error('Error deleting coupon:', err);
      throw err;
    }
  };

  const handleToggleCouponStatus = async (couponId: string) => {
    try {
      const coupon = coupons.find(c => c.id === couponId);
      if (!coupon) return;

      const updates: CouponUpdateData = {
        isActive: !coupon.isActive
      };

      await handleUpdateCoupon(couponId, updates);
    } catch (err) {
      console.error('Error toggling coupon status:', err);
      throw err;
    }
  };

  const handleUpdateSettings = async (settingsUpdates: Partial<CouponSettings>) => {
    try {
      const updatedSettings = await couponService.updateCouponSettings(settingsUpdates);
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Error updating coupon settings:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="coupons-page">
        <div className="coupons-page__loading">
          <div className="coupons-page__spinner">
            <div className="coupons-page__spinner-circle"></div>
          </div>
          <h2>Loading Coupons...</h2>
          <p>Please wait while we fetch your coupon data.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="coupons-page">
        <div className="coupons-page__error">
          <div className="coupons-page__error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2>Error Loading Coupons</h2>
          <p>{error}</p>
          <button 
            onClick={loadCouponsData} 
            className="coupons-page__retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="coupons-page">
      <CouponManager
        coupons={coupons}
        settings={settings}
        loading={loading}
        onCreateCoupon={handleCreateCoupon}
        onUpdateCoupon={handleUpdateCoupon}
        onDeleteCoupon={handleDeleteCoupon}
        onToggleCouponStatus={handleToggleCouponStatus}
        onUpdateSettings={handleUpdateSettings}
      />
    </div>
  );
};

export default CouponsPage;
